import { LegacyRoute } from '../routesMiddleware';
import ApprovalsRoute from './ApprovalsRoute';
import EngagementRoute from './EngagementRoute';
import EngagementsRoute from './EngagementsRoute';

/**
 * Consulting engagements (ENG-5448 / epic ENG-5399).
 *
 * The routes are always registered; `useConsultingEngagementsEnabled` is checked
 * in each route component, which renders the 404 page when the feature is dark.
 * Gating in the component rather than the route table means the allowlist takes
 * effect without rebuilding the route tree. (ENG-5607 moved that gate off
 * GrowthBook onto a per-env account allowlist.)
 *
 * Three surfaces, all product routes:
 *  - `/engagements`      the viewer's list, both sides. `myEngagementBuckets`
 *                        is viewer-aware as of ENG-5609, so a customer lands on
 *                        their own engagements rather than being sent to
 *                        Approvals.
 *  - `/approvals`        the governance approver's inbox.
 *  - `/engagements/:id`  the Hub.
 *
 * The inbox is top-level, NOT `/engagements/approvals` (ENG-5642). Nested there
 * it collided with the nav's active rule: `NavBar/isNavItemActive` marks an item
 * active on `pathname === to || pathname.startsWith(to + '/')`, so the approvals
 * page lit up Engagements as well as Approvals. That prefix rule is wanted — it
 * is what keeps Engagements lit on `/engagements/:id` — and the inbox is a
 * sibling job rather than a child of the list, so moving it out is the honest
 * fix. Do not nest a sibling surface under `/engagements` again without reading
 * that rule first. The old path keeps a 301 in `routes/index.tsx`, since
 * `/engagements/:id` would otherwise absorb it into a dead-end Hub.
 *
 * `:id` therefore has no static sibling left to rank against, and the bare
 * `/engagements` index cannot be shadowed by it either, since `:id` requires a
 * segment to match.
 *
 * There is deliberately no `/engagements/new`. Creation is reached from the
 * project tab, the candidate card and the profile page (FE3/FE6) — a
 * query-param URL standing in for those CTAs is test plumbing and does not
 * belong in the product's route table. `HireExpertDrawer` itself stays, covered
 * by `drawers.test.tsx` and its Storybook story until those CTAs land.
 */
const engagements: LegacyRoute = {
  path: '/engagements',

  async action() {
    document.title = 'Engagements';
    return <EngagementsRoute />;
  },
};

const approvals: LegacyRoute = {
  path: '/approvals',

  async action() {
    document.title = 'Approvals';
    return <ApprovalsRoute />;
  },
};

const engagement: LegacyRoute = {
  path: '/engagements/:id',

  async action() {
    document.title = 'Engagement';
    return <EngagementRoute />;
  },
};

const routes: LegacyRoute[] = [engagements, approvals, engagement];

export default routes;
